// src/App.js
import React, { useState, Suspense, lazy, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';
import LanguagePicker from './components/LanguagePicker';
import LanguageBanner from './components/LanguageBanner';
import FooterDoodle from './components/FooterDoodle';
import './App.css';
import useResourceTypes from './hooks/useResourceTypes';
import { T } from './lib/gt-shim';
import { ResourceCreatePage } from './pages/ResourceEditPage';

// Lazy load components
const HomePage = lazy(() => import('./pages/HomePage'));
const ResourceSearchPage = lazy(() => import('./pages/ResourceSearchPage'));
const ResourceDetailPage = lazy(() => import('./pages/ResourceDetailPage'));
const MapView = lazy(() => import('./components/MapView'));
const EligibilityScreener = lazy(() => import('./pages/EligibilityScreener'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdminDataLoader = lazy(() => import('./pages/AdminDataLoader'));
const AdminResourcesPage = lazy(() => import('./pages/AdminResourcesPage'));
const ResourceEditPage = lazy(() => import('./pages/ResourceEditPage'));

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Language UX: the language affordance is COLLAPSED by default. A persistent
  // "Read this in …" strip across the top on first load was too loud a welcome
  // — it demanded a choice before the visitor could read anything, and it
  // pushed the whole page down on first paint. Instead, the navbar globe is the
  // single, always-present, unobtrusive way to choose a language at any time:
  // it opens the full LanguagePicker modal (focus-trapped, centered, causing no
  // layout shift) from any route.
  //
  // The lightweight LanguageBanner surface is retained (mounted, fully wired)
  // but stays CLOSED by default — it is no longer auto-opened on first load.
  // Nothing pushes the page down on first paint anymore.
  const [languagePickerOpen, setLanguagePickerOpen] = useState(false);
  const [languageBannerOpen, setLanguageBannerOpen] = useState(false);
  useResourceTypes(); // Prefetch categories on app load

  const openLanguagePicker = useCallback(() => setLanguagePickerOpen(true), []);
  const closeLanguagePicker = useCallback(() => setLanguagePickerOpen(false), []);
  const dismissLanguageBanner = useCallback(() => setLanguageBannerOpen(false), []);
  // "More" in the banner: close the banner, open the full modal.
  const openPickerFromBanner = useCallback(() => {
    setLanguageBannerOpen(false);
    setLanguagePickerOpen(true);
  }, []);

  return (
    <Router>
        <div className="app">
          {/* Global "ink-wobble" filter — a faint hand-drawn tremble for SVG
              line art (the cartographic horizon, doodles). Defined once here,
              referenced via filter: url(#ink-wobble). Static seed (no animated
              boil), so it's inherently reduced-motion safe. Decorative only —
              never applied to text or form controls. */}
          <svg
            width="0"
            height="0"
            aria-hidden="true"
            focusable="false"
            style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
            <defs>
              {/* Cheap by construction: numOctaves=1 (single noise pass) and a
                  tight ~110% region so the few remaining LIVE uses (small,
                  high-value accents only — the big full-width skyline/river-seam
                  and the repeated panel frames now bake their tremble into the
                  path geometry instead) cost a fraction of a repaint. Static
                  seed → no animated boil, reduced-motion safe. */}
              <filter id="ink-wobble" x="-5%" y="-5%" width="110%" height="110%">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.018 0.022"
                  numOctaves="1"
                  seed="7"
                  result="noise"
                />
                <feDisplacementMap
                  in="SourceGraphic"
                  in2="noise"
                  scale="2.2"
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>
            </defs>
          </svg>
          <LanguageBanner
            open={languageBannerOpen}
            onDismiss={dismissLanguageBanner}
            onOpenPicker={openPickerFromBanner}
          />
          <Navbar
            isLoggedIn={isLoggedIn}
            onOpenLanguagePicker={openLanguagePicker}
            /* The navbar globe is the single, always-present language
               affordance. The banner no longer auto-opens on first visit, so
               there is no competing surface to suppress it for — the globe is
               available on every route, every visit. */
            showLanguageTrigger
          />
          <main className="container">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/search" element={<ResourceSearchPage />} />
                <Route path="/resource/:id" element={<ResourceDetailPage />} />
                <Route path="/map" element={<MapView />} />
                <Route path="/eligibility" element={<EligibilityScreener />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/login" element={<LoginPage onLogin={setIsLoggedIn} />} />
                <Route path="/admin" element={<ProtectedRoute isLoggedIn={isLoggedIn}><AdminDataLoader /></ProtectedRoute>} />
                <Route path="/admin/resources" element={<ProtectedRoute isLoggedIn={isLoggedIn}><AdminResourcesPage /></ProtectedRoute>} />
                <Route path="/admin/resources/create" element={<ProtectedRoute isLoggedIn={isLoggedIn}><ResourceCreatePage /></ProtectedRoute>} />
                <Route path="/admin/resources/edit/:id" element={<ProtectedRoute isLoggedIn={isLoggedIn}><ResourceEditPage /></ProtectedRoute>} />
              </Routes>
            </Suspense>
          </main>
          <footer className="footer">
            <div className="container">
              {/* One centered column on the page's plumb line: the rotating
                  landmark doodle leads (sitting directly under the centered "518"
                  endmark above it, like ink dropping from a nib), then a warm
                  sign-off, then the mission + links + fine print. A blind creative
                  panel (6/6) found the old 3-island row drifted left-of-center and
                  the doodle never landed under the stamp. */}
              <div className="footer-content">
                <FooterDoodle />
                <div className="footer-links">
                  <Link to="/about"><T>About</T></Link>
                  <a href="mailto:contact@518cares.org"><T>Contact</T></a>
                </div>
                <div className="footer-copyright">
                  {/* One ruled fine-print line. "free clinic & resource directory"
                      restores the trust signal a low-income user scrolls down for
                      (was lost with the mission tagline) at zero added height on this
                      fixed line. The translatable copy stays in a TEXT-ONLY <T> (mixing
                      JSX into <T> is a no-op for the translation engine — see
                      translate-engine.test.js); only the inline heart + name link sit
                      outside it. */}
                  <p>
                    <T>© 2026 518 Cares · Troy, NY · free clinic &amp; resource directory · Created with</T>{' '}
                    <span role="img" aria-label="love" style={{color: 'var(--coral-800)', fontSize: '1.1em', verticalAlign: 'middle'}}>&#9829;</span>{' '}
                    <T>by</T>{' '}
                    <a href="https://www.linkedin.com/in/michaeljpaglia/" target="_blank" rel="noopener noreferrer" style={{color: 'var(--coral-800)', textDecoration: 'underline'}}>Michael Paglia</a>
                  </p>
                </div>
              </div>
            </div>
          </footer>
          <LanguagePicker open={languagePickerOpen} onClose={closeLanguagePicker} />
        </div>
      </Router>
  );
}

export default App;
